import { useMemo, useState } from 'react'
import { UsersIcon } from 'lucide-react'
import { useClubParticipationReport } from '@/api/hooks'
import { clubsWithStanding } from '@/components/club-awards'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import {
  getDefaultDirection,
  nextClubSort,
  sortClubRows,
  type ClubReportSort,
  type ClubReportSortKey,
} from '../reports/club-participation-sort'
import { ClubSortHeader } from '../reports/ClubSortHeader'

/** Named clubs with entries or points, from the same standings as the club awards. */
export function ClubLeaderboardCard({ showId, showName }: { showId: string; showName: string }) {
  const [sort, setSort] = useState<ClubReportSort>({
    key: 'points',
    direction: getDefaultDirection('points'),
  })
  const query = useClubParticipationReport(showId, { includeUnknown: false })
  const numberFormatter = useMemo(() => new Intl.NumberFormat(), [])
  const rows = useMemo(
    () => sortClubRows(clubsWithStanding(query.data?.rows ?? []), sort.key, sort.direction),
    [query.data, sort]
  )
  const onSort = (key: ClubReportSortKey) => setSort(current => nextClubSort(current, key))

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <UsersIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
          <CardTitle>Club leaderboard</CardTitle>
        </div>
        <CardDescription>
          Entries and placement points for each club at{' '}
          <span className="font-semibold text-primary">{showName}</span>. Club Spirit goes to the
          most entries and Club Champion to the most points.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* A failed refresh keeps the last standings on screen; the next poll retries. */}
        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading club leaderboard…</p>
        ) : query.isError && !query.data ? (
          <p className="text-sm text-destructive">
            {query.error?.message ?? 'Failed to load the club leaderboard.'}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No club has entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <ClubSortHeader label="Club" sortKey="name" sort={sort} onSort={onSort} />
                  <ClubSortHeader
                    label="Entries"
                    sortKey="entries"
                    sort={sort}
                    onSort={onSort}
                    align="right"
                  />
                  <ClubSortHeader
                    label="Points"
                    sortKey="points"
                    sort={sort}
                    onSort={onSort}
                    align="right"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={`${row.clubId ?? 'none'}-${row.clubName}`}>
                    <TableCell className="font-medium">{row.clubName}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {numberFormatter.format(row.entryCount)}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {numberFormatter.format(row.totalPoints)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
